import { computeConsolidatorRefundFee } from './consolidator-fee'
import type { ConsolidatorFeeBreakdown } from './consolidator-fee'
import { CONSOLIDATOR_REFUND_FEE } from './constants'
import { roundCurrency } from './money'
import type { AirlineDeductions } from './types'

/**
 * The two-ledger refund statement (SPEC.md §1). Pure, branch-free arithmetic: special cases
 * (void window, non-refundable, partial selection, multi-currency) arrive pre-resolved in the
 * inputs — this module never inspects them.
 */

export interface RefundStatementInput {
  /** What PLT paid the supplier for the cancelled tickets, gross of taxes */
  nettPaidToSupplier: number
  /** What the passenger paid, pro-rated to the selection for partial cancellations */
  totalPaidByPax: number
  deductions: AirlineDeductions
  /** Consolidator fee — supplier ledger ONLY. */
  supplierFee: number
  /** PLT termination fee — pax ledger ONLY. */
  pltFee: number
}

export interface RefundStatement {
  /** max(0, supplierLedger.raw) — the cash PLT expects to receive from the supplier. */
  expectedSupplierRefund: number
  /** clamp(paxLedger.raw, 0, totalPaid) — the cash promised to the customer. */
  refundToPax: number
  supplierLedger: { nettPaid: number; deductions: number; supplierFee: number; raw: number }
  paxLedger: { totalPaid: number; deductions: number; pltFee: number; raw: number }
  /**
   * paxLedger.raw − supplierLedger.raw. Identity (asserted over every fixture):
   * margin = (totalPaid − nettPaid) − pltFee + supplierFee. Breaks iff a penalty, tax, or fee
   * is double-counted on one ledger — the structural guard against formula drift.
   */
  margin: number
  /** paxLedger.raw < 0 — the customer owes money; surfaced, never silently floored away. */
  isShortfall: boolean
}

export function computeRefundStatement(input: RefundStatementInput): RefundStatement {
  const deductions = input.deductions.total
  const supplierRaw = roundCurrency(input.nettPaidToSupplier - deductions - input.supplierFee)
  const paxRaw = roundCurrency(input.totalPaidByPax - deductions - input.pltFee)

  return {
    expectedSupplierRefund: Math.max(0, supplierRaw),
    refundToPax: Math.min(Math.max(0, paxRaw), roundCurrency(input.totalPaidByPax)),
    supplierLedger: {
      nettPaid: roundCurrency(input.nettPaidToSupplier),
      deductions,
      supplierFee: roundCurrency(input.supplierFee),
      raw: supplierRaw,
    },
    paxLedger: {
      totalPaid: roundCurrency(input.totalPaidByPax),
      deductions,
      pltFee: roundCurrency(input.pltFee),
      raw: paxRaw,
    },
    margin: roundCurrency(paxRaw - supplierRaw),
    isShortfall: paxRaw < 0,
  }
}

export interface ResolvePltFeeParams {
  cancellingPax: number
  totalPax: number
  salesPrice: number
  isVoidWindow: boolean
  noTicketsIssued: boolean
  /** Partial cancellation of a non-refundable ticket: the booking continues, no termination fee. */
  isPartialNonRefundable?: boolean
  override?: number
}

/**
 * min(150 × cancellingPax, 0.1 × salesPrice × cancellingPax / totalPax) — for a full cancellation
 * (cancellingPax = totalPax) this reduces to the historical min(150 × pax, 10% × TTV).
 */
export function resolvePltFee(p: ResolvePltFeeParams): number {
  if (p.override !== undefined) return p.override

  if (p.isVoidWindow || p.noTicketsIssued || p.isPartialNonRefundable) return 0

  if (p.cancellingPax <= 0 || p.salesPrice <= 0) return 0

  const totalPax = p.totalPax > 0 ? p.totalPax : p.cancellingPax
  return roundCurrency(Math.min(150 * p.cancellingPax, (0.1 * p.salesPrice * p.cancellingPax) / totalPax))
}

export interface ResolveSupplierFeeParams {
  owningPcc?: string
  ticketingPcc?: string
  /** Cancelling ADT + CHD passengers. */
  adtChdCount: number
  infCount: number
  /** Sum of the cancelling tickets' issuance commission (booking currency). */
  commissionClawback: number
  /** 0.10 AU-domestic / 0.15 NZ-domestic / 0 — resolve with resolveDomesticGSTRate(). */
  gstRate: number
  /** Untied PROCFE processing fees included in NettPaid. */
  processingFees: number
  isVoidWindow: boolean
  noTicketsIssued: boolean
}

const NO_SUPPLIER_FEE: ConsolidatorFeeBreakdown = {
  flatFee: 0,
  gst: 0,
  commissionClawback: 0,
  processingFees: 0,
  total: 0,
}

/** Consolidator refund charge; all-zeros when no CONSOLIDATOR_REFUND_FEE rule matches the PCC pair. */
export function resolveSupplierFee(p: ResolveSupplierFeeParams): ConsolidatorFeeBreakdown {
  if (p.isVoidWindow || p.noTicketsIssued) return NO_SUPPLIER_FEE
  if (!p.owningPcc || !p.ticketingPcc) return NO_SUPPLIER_FEE

  const rule = CONSOLIDATOR_REFUND_FEE[`${p.owningPcc} (${p.ticketingPcc})`]
  if (!rule) return NO_SUPPLIER_FEE

  return computeConsolidatorRefundFee(rule, {
    adtChdCount: p.adtChdCount,
    infCount: p.infCount,
    commission: p.commissionClawback,
    gstRate: p.gstRate,
    processingFees: p.processingFees,
  })
}
